// BUILD VERIFICATION - Remove after debugging
const BUILD_TIMESTAMP = '{{BUILD_TIME}}';
const BUILD_VERSION = 'v2024-06-10-FORCE-API-KEY';
console.log('🏗️ BUILD VERIFICATION:', {
  buildTimestamp: new Date().toISOString(),
  buildVersion: BUILD_VERSION,
  location: 'apiService.ts',
  apiKeyHardcoded: true
});

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosHeaders, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { Client, BannerSet, User, AuthState, LoginCredentials, LoginResponse } from '../types';
import { getApiConfig, getAuthConfig } from '../config';
import { config } from '../config';

interface ApiResponse<T = any> {
  data: T;
  status: number;
  headers: Record<string, string>;
}

class ApiService {
  public api: AxiosInstance;
  private retryCount: number = 3;
  private retryDelay: number = 1000; // 1 second
  private apiKey: string;
  private readonly SAVE_TIMEOUT = 30000; // 30 seconds for save operations
  private readonly DEFAULT_TIMEOUT = 10000; // 10 seconds for other operations

  constructor() {
    // DEPLOYMENT DEBUG - Check environment
    console.log('🌍 ENVIRONMENT DEBUG:', {
      currentURL: window.location.href,
      hostname: window.location.hostname,
      isCloudflarePages: window.location.hostname.includes('.pages.dev'),
      isDealercentiveDomain: window.location.hostname.includes('dealercentive'),
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString()
    });

    // DOMAIN VERIFICATION
    console.log('🌐 DOMAIN VERIFICATION:', {
      currentDomain: window.location.hostname,
      apiDomain: 'api.dealercentive.us',
      allowedOrigins: [
        'https://dealercentives-banner-editor.pages.dev',
        'https://banners.dealercentive.us'
      ],
      corsCheck: 'Will be verified in network tab'
    });

    // FORCE API KEY - Emergency fix
    this.apiKey = '7aa03210fd5b9a2f9f033d3ecc63f5033d50b';

    console.log('🚨 ApiService Debug - FORCED API KEY:', {
      configApiKey: config.api.apiKey,
      forcedApiKey: this.apiKey,
      apiKeyLength: this.apiKey?.length,
      baseURL: config.api.baseUrl
    });

    this.api = axios.create({
      baseURL: config.api.baseUrl.endsWith('/v1') ? config.api.baseUrl : `${config.api.baseUrl}/v1`,
      timeout: this.DEFAULT_TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': this.apiKey  // This will now be the hardcoded key
      }
    });

    // Add request interceptor for logging and timeout adjustment
    this.api.interceptors.request.use(
      (config) => {
        // Show actual API key being sent
        console.log('🔍 Request Headers - ACTUAL API KEY:', {
          url: config.url,
          method: config.method,
          'X-API-Key-Length': config.headers['X-API-Key']?.length,
          'X-API-Key-First8': config.headers['X-API-Key']?.substring(0, 8),
          'X-API-Key-Full': config.headers['X-API-Key'], // Show full key for debugging
          baseURL: config.baseURL,
          fullURL: `${config.baseURL}${config.url}`,
          headers: config.headers
        });

        // Set longer timeout for save operations
        if (config.method === 'put' || config.method === 'post') {
          config.timeout = this.SAVE_TIMEOUT;
        }

        return config;
      },
      (error) => {
        console.error('🔴 API Request Error:', error);
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error logging
    this.api.interceptors.response.use(
      (response) => {
        console.log('✅ API Response:', {
          url: response.config.url,
          method: response.config.method,
          status: response.status,
          statusText: response.statusText,
          headers: response.headers
        });
        return response;
      },
      (error) => {
        console.error('🔴 API Response Error:', {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          headers: error.config?.headers,
          baseURL: error.config?.baseURL,
          fullURL: `${error.config?.baseURL}${error.config?.url}`
        });
        return Promise.reject(error);
      }
    );
  }

  private async retryRequest<T>(
    requestFn: () => Promise<T>,
    retries: number = this.retryCount
  ): Promise<T> {
    try {
      return await requestFn();
    } catch (error) {
      if (retries > 0 && this.isRetryableError(error)) {
        console.log(`🟡 Retrying request (${retries} attempts remaining)...`);
        await new Promise(resolve => setTimeout(resolve, this.retryDelay));
        return this.retryRequest(requestFn, retries - 1);
      }
      throw error;
    }
  }

  private isRetryableError(error: any): boolean {
    if (error instanceof AxiosError) {
      // Retry on network errors, timeouts, or 5xx server errors
      return !error.response || 
             error.code === 'ECONNABORTED' ||
             (error.response.status >= 500 && error.response.status < 600);
    }
    return false;
  }

  // Banner Set methods
  async getBannerSets(): Promise<BannerSet[]> {
    try {
      const response = await this.api.get<BannerSet[]>('/banner-sets');
      return response.data;
    } catch (error) {
      console.error('Error fetching banner sets:', error);
      throw error;
    }
  }

  async getBannerSet(id: string): Promise<BannerSet> {
    try {
      const response = await this.api.get<BannerSet>(`/banner-sets/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching banner set ${id}:`, error);
      throw error;
    }
  }

  async createBannerSet(bannerSet: Omit<BannerSet, 'id'>): Promise<BannerSet> {
    try {
      const response = await this.api.post<BannerSet>('/banner-sets', bannerSet);
      return response.data;
    } catch (error) {
      console.error('Error creating banner set:', error);
      throw error;
    }
  }

  async updateBannerSet(id: string, updates: Partial<BannerSet>): Promise<BannerSet> {
    try {
      // Try to get current banner set
      let currentBannerSet: BannerSet | null = null;
      try {
        currentBannerSet = await this.getBannerSet(id);
      } catch (error: any) {
        // If banner set doesn't exist (404), create it
        if (error.response?.status === 404) {
          console.log('Banner set not found, creating new one:', id);
          const newBannerSet: Omit<BannerSet, 'id'> = {
            name: updates.name || 'New Banner Set',
            description: updates.description || '',
            sizes: updates.sizes || [],
            dataFeed: updates.dataFeed || null,
            dataFeedArray: updates.dataFeedArray || [],
            jsonFeedUrl: updates.jsonFeedUrl,
            createdAt: new Date(),
            lastModified: new Date(),
            status: updates.status || 'draft',
            version: 1
          };
          return await this.createBannerSet(newBannerSet);
        }
        throw error;
      }
      
      // If banner set exists, update it
      const updatedData = {
        ...updates,
        version: (currentBannerSet.version || 0) + 1
      };

      const response = await this.api.put<BannerSet>(`/banner-sets/${id}`, updatedData);
      return response.data;
    } catch (error) {
      console.error(`Error updating banner set ${id}:`, error);
      throw error;
    }
  }

  async deleteBannerSet(id: string): Promise<void> {
    try {
      await this.api.delete(`/banner-sets/${id}`);
    } catch (error) {
      console.error(`Error deleting banner set ${id}:`, error);
      throw error;
    }
  }

  // Client methods
  async getClients(): Promise<Client[]> {
    return this.retryRequest(() =>
      this.api.get<Client[]>('/api/clients')
        .then(response => response.data)
    );
  }

  async getClient(id: string): Promise<Client> {
    return this.retryRequest(() =>
      this.api.get<Client>(`/api/clients/${id}`)
        .then(response => response.data)
    );
  }

  // User methods
  async getUser(id: string): Promise<User> {
    return this.retryRequest(() =>
      this.api.get<User>(`/api/users/${id}`)
        .then(response => response.data)
    );
  }

  async createUser(user: Omit<User, 'id'>): Promise<User> {
    return this.retryRequest(() =>
      this.api.post<User>('/api/users', user)
        .then(response => response.data)
    );
  }

  // ... rest of the implementation ...
}

// Create a single instance
const apiInstance = new ApiService();

// Export the instance methods
export const apiService = {
  getBannerSets: () => apiInstance.getBannerSets(),
  getBannerSet: (id: string) => apiInstance.getBannerSet(id),
  createBannerSet: (bannerSet: Omit<BannerSet, 'id'>) => apiInstance.createBannerSet(bannerSet),
  updateBannerSet: (id: string, updates: Partial<BannerSet>) => apiInstance.updateBannerSet(id, updates),
  deleteBannerSet: (id: string) => apiInstance.deleteBannerSet(id)
};

// DEBUG: Comprehensive deployment verification
export const testDeployment = async () => {
  console.log('🔍 DEPLOYMENT VERIFICATION TEST');
  
  // Test 1: Verify API service is using correct values
  console.log('📋 Step 1 - API Service Configuration:', {
    apiInstanceExists: !!apiInstance,
    apiKeySet: !!apiInstance['apiKey'],
    apiKeyValue: apiInstance['apiKey'],
    baseURL: apiInstance.api.defaults.baseURL,
    headers: apiInstance.api.defaults.headers
  });

  // Test 2: Health check endpoint
  console.log('📋 Step 2 - Testing Health Endpoint...');
  try {
    const healthResponse = await fetch('https://api.dealercentive.us/v1/health', {
      headers: {
        'X-API-Key': '7aa03210fd5b9a2f9f033d3ecc63f5033d50b',
        'Content-Type': 'application/json'
      }
    });
    
    const healthData = await healthResponse.json();
    console.log('✅ Health Check Result:', {
      status: healthResponse.status,
      statusText: healthResponse.statusText,
      data: healthData
    });
  } catch (error) {
    console.error('❌ Health Check Failed:', error);
  }

  // Test 3: Direct banner set API call
  console.log('📋 Step 3 - Testing Banner Set API...');
  try {
    const bannerResponse = await fetch('https://api.dealercentive.us/v1/banner-sets/set-1', {
      method: 'GET',
      headers: {
        'X-API-Key': '7aa03210fd5b9a2f9f033d3ecc63f5033d50b',
        'Content-Type': 'application/json'
      }
    });
    
    console.log('📊 Banner Set API Result:', {
      status: bannerResponse.status,
      statusText: bannerResponse.statusText,
      headers: Object.fromEntries(bannerResponse.headers.entries())
    });

    if (bannerResponse.ok) {
      const bannerData = await bannerResponse.json();
      console.log('✅ Banner Set Data:', bannerData);
    } else {
      const errorData = await bannerResponse.text();
      console.log('❌ Banner Set Error Response:', errorData);
    }
  } catch (error) {
    console.error('❌ Banner Set API Failed:', error);
  }

  // Test 4: Verify axios instance configuration
  console.log('📋 Step 4 - Axios Instance Verification:', {
    axiosBaseURL: apiInstance.api.defaults.baseURL,
    axiosHeaders: apiInstance.api.defaults.headers,
    axiosTimeout: apiInstance.api.defaults.timeout
  });

  console.log('🏁 DEPLOYMENT VERIFICATION COMPLETE');
};

// DEBUG: Test actual save operation with full logging
export const testSaveOperation = async () => {
  try {
    // Create a new banner set with a unique ID
    const newBannerSet = {
      name: 'Test Banner Set',
      description: 'Test banner set created for system health check',
      sizes: [],
      dataFeed: null,
      dataFeedArray: [],
      createdAt: new Date(),
      lastModified: new Date(),
      status: 'draft' as const
    };

    // Create the banner set first
    const createdBannerSet = await apiService.createBannerSet(newBannerSet);
    console.log('Created test banner set:', createdBannerSet);

    // Now try to update it
    const updates = {
      name: 'Updated Test Banner Set',
      lastModified: new Date()
    };

    const updatedBannerSet = await apiService.updateBannerSet(createdBannerSet.id, updates);
    console.log('Updated test banner set:', updatedBannerSet);

    return {
      success: true,
      message: 'Test save operation completed successfully',
      data: updatedBannerSet
    };
  } catch (error) {
    console.error('Test save operation failed:', error);
    return {
      success: false,
      message: 'Test save operation failed',
      error
    };
  }
};

// Make test functions available globally for console testing
if (typeof window !== 'undefined') {
  (window as any).testDeployment = testDeployment;
  (window as any).testSaveOperation = testSaveOperation;
  (window as any).apiInstance = apiInstance;
} 